import React, { useCallback, useEffect, useMemo } from 'react';
import syncRowHeights from '../../../../helpers/syncRowHeights';
import { GenericTable } from '../GenericTable';
import VerifyButton from '../../../verify-button/verifyButton';

export const CMOSFormatted = ({ translations, summaryData, setSummaryData, selectedMatches, prefix, disabledByUserType, userInput, setUserInput, meterInput, setConfirmModal, qualityAssurance }) => {
  useEffect(() => {
    setUserInput(summaryData)
  }, [summaryData.Core_SPID])

  const handleUpdateOnUprn = () => {
    if (!selectedMatches.abp) return
    const choice = selectedMatches.abp
    var SAO = '';
    if (!choice.SUB_BUILDING && !choice.BUILDING_NAME && !choice.BUILDING_NUMBER) {
      SAO = choice.ORGANISATION
    }
    var combinedName = ''
    if (choice.STREET_NAME && choice.BUILDING_NAME && choice.BUILDING_NUMBER) {
      combinedName = choice.BUILDING_NUMBER + ' ' + choice.BUILDING_NAME
    }
    setUserInput({
      ...userInput,
      CF_UPRN: choice.UPRN,
      CF_UPRN_Reason_Code: '',
      CF_Secondary_Addressable_Object: SAO || choice.SUB_BUILDING || '',
      CF_Primary_Addressable_Object: combinedName ? combinedName : choice.STREET_NAME ? (choice.BUILDING_NAME || choice.BUILDING_NUMBER || '') : '',
      CF_Address_Line_1: choice.STREET_NAME || choice.BUILDING_NAME || choice.BUILDING_NUMBER || '',
      CF_Address_Line_2: choice.LOCALITY ?? '',
      CF_Address_Line_3: choice.TOWN_NAME ?? '',
      CF_Address_Line_4: choice.POST_TOWN ?? '',
      CF_Address_Line_5: choice.ISLAND ?? '',
      CF_Postcode: choice.POSTCODE ?? ''
    })
    syncRowHeights()
  }

  const handleUpdateOnVoaOrCt = () => {
    const source = selectedMatches.voa ? "voa" : selectedMatches.ct ? "ct" : ""
    const choice = selectedMatches[source]
    if (source === "voa") {
      const useScottishTerms =  ["SCOTTISH-W", "SW"].includes(choice.Wholesaler)
      setUserInput({
        ...userInput,
        CF_VOA: choice.BA_Reference_Number,
        CF_VOA_Reason_Code: '',
        CF_Secondary_Addressable_Object: '',
        CF_Primary_Addressable_Object: useScottishTerms ? (choice.STREET && (choice.PAON ?? '') ) : (choice.Street && (choice.Number_Or_Name ?? '') ),
        CF_Address_Line_1: useScottishTerms ? choice.STREET || choice.PAON || '' : choice.Street || choice.Number_Or_Name || '',
        CF_Address_Line_2: '',
        CF_Address_Line_3: useScottishTerms ? choice.ADMIN_AREA ?? '' : choice.Postal_District ?? '',
        CF_Address_Line_4: useScottishTerms ? choice.TOWN  ?? '' : choice.Town ?? '',
        CF_Address_Line_5: useScottishTerms ? choice.UNITARY_AUTHORITY ?? '' : choice.County ?? '',
        CF_Postcode: choice.Postcode ?? ''
      })
    } else if (source === "ct") {
      const formattedAddress = {}
      const fields = [
        'CT_Address_Line_1',
        'CT_Address_Line_2',
        'CT_Address_Line_3',
        'CT_Address_Line_4',
        'CT_Address_Line_5',
      ];

      const addressLines = [
        'CF_Address_Line_1',
        'CF_Address_Line_2',
        'CF_Address_Line_3',
        'CF_Address_Line_4',
        'CF_Address_Line_5',
      ];

      const components = fields.map(field => selectedMatches[source][field]).filter(value => value && value.trim() !== '');

      components.forEach((component, index) => {
        if (index < addressLines.length) {
          formattedAddress[addressLines[index]] = component;
        }
      });
      setUserInput({
        ...userInput,
        CF_Secondary_Addressable_Object: '',
        CF_Primary_Addressable_Object: '',
        CF_Address_Line_1: choice.CT_Address_Line_1 ?? '',
        CF_Address_Line_2: choice.CT_Address_Line_2 ??'',
        CF_Address_Line_3: choice.CT_Address_Line_3 ?? '',
        CF_Address_Line_4: choice.CT_Address_Line_4 ?? '',
        CF_Address_Line_5: choice.CT_Address_Line_5 ?? '',
        CF_Postcode: choice.postcode ?? ''
      })
    }
    syncRowHeights()
  }

  const handleInputChange = useCallback((newInput) => {
    if (!newInput) return
    if (newInput.CF_UPRN_Reason_Code) {
      newInput.CF_UPRN = ''
    } 
    if (newInput.CF_VOA_Reason_Code) {
      newInput.CF_VOA = ''
    }
    if (newInput.CF_UPRN) {
      newInput.CF_UPRN_Reason_Code = ''
    }
    if (newInput.CF_VOA) {
      newInput.CF_VOA_Reason_Code = ''
    }
    setUserInput(prevUserInput => ({
        ...prevUserInput,
        ...newInput,
    }));
  }, [setUserInput]);

  const rows = useMemo(() => {
    const abpMatchEnabled = Boolean(
      summaryData && selectedMatches.abp?.UPRN && String(selectedMatches.abp.UPRN) !== String(userInput.CF_UPRN)
    );
    
    const voaMatchEnabled = Boolean(
      summaryData && selectedMatches.voa?.BA_Reference_Number && String(selectedMatches.voa.BA_Reference_Number) !== String(userInput.CF_VOA)
    );
    
    const ctMatchEnabled = Boolean(
      summaryData && selectedMatches.ct?.au_reference_number && String(selectedMatches.ct.au_reference_number) !== String(userInput.au_reference_number)
    );
    
    return [
      [translations['CF_VOA_BA_Reference'], {hasInput: true, column: 'CF_VOA', value: userInput?.['CF_VOA'], disabled: disabledByUserType}],
      [translations['VOA_Reason_Code'], 
        {
          hasDropdown: true,
          column: 'CF_VOA_Reason_Code',
          value: userInput?.['CF_VOA_Reason_Code'],
          options: [
            { value: '', label: "None"},
            { value: 'AG', label: "No number - agricultural land and buildings"},
            { value: 'IP', label: "No number - infrastructure Project"},
            { value: 'ME', label: "No number - missing Entry from the VOA"},
            { value: 'MT', label: "Multiple VOA BA References at the same eligible premises"},
            { value: 'NR', label: "No number - property not yet rated"},
            { value: 'SP', label: "No number provided - use number from Other SPID"},
            { value: 'SR', label: "No number - fish farms, fishing, and sporting rights"},
            { value: 'OT', label: "Other"},
          ],
          disabled: disabledByUserType
        }
      ],
      [translations['CF_UPRN'], {hasInput: true, column: 'CF_UPRN', value: userInput?.['CF_UPRN'], disabled: disabledByUserType}],
      [translations['UPRN_Reason_Code'], 
        {
          hasDropdown: true, 
          column: 'CF_UPRN_Reason_Code', 
          value: userInput?.['CF_UPRN_Reason_Code'],
          options: [
            { value: '', label: "None"},
            { value: 'BW', label: "No number - construction site"},
            { value: 'IP', label: "No number - infrastructure Project"},
            { value: 'ME', label: "No number - missing Entry from the NLPG"},
            { value: 'MT', label: "Multiple UPRNs at the same eligible premises"},
            { value: 'PL', label: "No number - not yet issued by planning"},
            { value: 'SP', label: "No number provided - use number from Other SPID"},
            { value: 'SR', label: "No number - fish farms, fishing, and sporting rights"},
            { value: 'OT', label: "Other"},
          ],
          disabled: disabledByUserType
        }
      ],
      [translations['CF_Free_Descriptor'], {hasTextArea: true, column: 'CF_Free_Descriptor', value: userInput?.['CF_Free_Descriptor'], disabled: disabledByUserType}],
      [translations['Occupier'], {hasTextArea: true, column: 'Customer_Name', value: userInput?.['Customer_Name'], disabled: disabledByUserType}],
      [translations['CF_Secondary_Addressable_Object'], {hasTextArea: true, column: 'CF_Secondary_Addressable_Object', value: userInput?.['CF_Secondary_Addressable_Object'], disabled: disabledByUserType}],
      [translations['CF_Primary_Addressable_Object'], {hasTextArea: true, column: 'CF_Primary_Addressable_Object', value: userInput?.['CF_Primary_Addressable_Object'], disabled: disabledByUserType}],
      [translations['CF_Address_Line_1'], {hasTextArea: true, column: 'CF_Address_Line_1', value: userInput?.['CF_Address_Line_1'], disabled: disabledByUserType}],
      [translations['CF_Address_Line_2'], {hasTextArea: true, column: 'CF_Address_Line_2', value: userInput?.['CF_Address_Line_2'], disabled: disabledByUserType}],
      [translations['CF_Address_Line_3'], {hasTextArea: true, column: 'CF_Address_Line_3', value: userInput?.['CF_Address_Line_3'], disabled: disabledByUserType}],
      [translations['CF_Address_Line_4'], {hasTextArea: true, column: 'CF_Address_Line_4', value: userInput?.['CF_Address_Line_4'], disabled: disabledByUserType}],
      [translations['CF_Address_Line_5'], {hasTextArea: true, column: 'CF_Address_Line_5', value: userInput?.['CF_Address_Line_5'], disabled: disabledByUserType}],
      [translations['CF_Postcode'], {hasTextArea: true, column: 'CF_Postcode', value: userInput?.['CF_Postcode'], disabled: disabledByUserType}],
      [
        ({summaryData}) => 
          <button 
            disabled={disabledByUserType}
            className={`button compact smaller-text ${(voaMatchEnabled || ctMatchEnabled) && !disabledByUserType ? 'background-primary colour-white' : ''}`} 
            onClick={() => (voaMatchEnabled || ctMatchEnabled) && handleUpdateOnVoaOrCt()}
          >Update on {ctMatchEnabled ? "CT" : "VOA"}</button>,         
        ({}) => 
          <button 
            disabled={disabledByUserType}
            className={`button compact smaller-text ${abpMatchEnabled && !disabledByUserType && "background-primary colour-white"}`} 
            onClick={() => abpMatchEnabled && handleUpdateOnUprn()}
          >Update on UPRN</button>
      ]
    ]
  }, [translations, window.reloadSummaryData, selectedMatches, userInput, setUserInput, summaryData])
  return [
    <div style={{ display: 'grid' }}>
      <GenericTable title={translations['Updated_CMOS_Premises_Header']} rows={rows} data={userInput} handleInputChange={handleInputChange} userInput={userInput} />
      <VerifyButton summaryData={summaryData} setSummaryData={setSummaryData} userInput={userInput} meterUserInput={meterInput} setUserInput={setUserInput} prefix={prefix} setConfirmModal={setConfirmModal} qualityAssurance={qualityAssurance} disabled={disabledByUserType} />
    </div>
  ]
}
